/**
 * MRZ (Machine Readable Zone) Parser - Enhanced Version
 * Supports TD1 (ID Cards like CIE), TD2, and TD3 (Passports) formats
 * 
 * Italian CIE uses TD1 format (3 lines, 30 characters each)
 * Passports use TD3 format (2 lines, 44 characters each)
 */

// Country code to nationality mapping
const COUNTRY_TO_NATIONALITY = {
  'ITA': 'Italiana',
  'DEU': 'Tedesca',
  'FRA': 'Francese',
  'GBR': 'Inglese',
  'ESP': 'Spagnola',
  'USA': 'Americana',
  'NLD': 'Olandese',
  'BEL': 'Belga',
  'CHE': 'Svizzera',
  'AUT': 'Austriaca',
  'PRT': 'Portoghese',
  'POL': 'Polacca',
  'ROU': 'Rumena',
  'UKR': 'Ucraina',
  'RUS': 'Russa',
  'CHN': 'Cinese',
  'JPN': 'Giapponese',
  'BRA': 'Brasiliana',
  'ARG': 'Argentina',
  'CAN': 'Canadese',
  'AUS': 'Australiana'
};

/**
 * Common OCR character substitutions for MRZ
 */
function fixOCRErrors(text) {
  return text
    .replace(/[oO]/g, '0')     // O -> 0
    .replace(/[lI|]/g, '1')    // l, I, | -> 1
    .replace(/[sS]/g, '5')     // S -> 5 (only in numeric contexts)
    .replace(/[zZ]/g, '2')     // Z -> 2 (only in numeric contexts)
    .replace(/[bB]/g, '8')     // B -> 8 (only in numeric contexts)
    .replace(/[gG]/g, '9')     // G -> 9 (only in numeric contexts)
    .replace(/«/g, '<')        // Common OCR for <
    .replace(/»/g, '<')
    .replace(/‹/g, '<')
    .replace(/›/g, '<')
    .replace(/[^\nA-Z0-9<]/gi, ''); // Remove invalid chars but keep newlines
}

/**
 * Try to find MRZ-like lines in OCR text
 * More lenient than strict parsing
 */
function findMRZLines(text) {
  console.log('Raw OCR text:', text);
  
  // Split into lines and clean each one
  const allLines = text.split(/[\n\r]+/).map(line => {
    // Fix common OCR errors and clean
    let cleaned = line.toUpperCase().trim();
    cleaned = fixOCRErrors(cleaned);
    return cleaned;
  }).filter(line => line.length > 0);
  
  console.log('Cleaned lines:', allLines);
  
  // Look for lines that look like MRZ (contain << or multiple < in sequence, or start with specific patterns)
  const mrzCandidates = allLines.filter(line => {
    // Must be reasonably long
    if (line.length < 20) return false;
    
    // Should contain < characters (filler in MRZ)
    const hasFillers = line.includes('<') || line.includes('«');
    
    // Or looks like it starts with document type (I, P, C, A for ID, Passport, etc.)
    const startsLikeDoc = /^[IPCA][A-Z<]/.test(line);
    
    // Or contains typical MRZ patterns (country codes, dates)
    const hasTypicalPattern = /[A-Z]{3}[0-9]{6,}/.test(line) || // Country + numbers
                              /[0-9]{6}[MF<][0-9]{6}/.test(line); // DOB + Sex + Expiry
    
    return hasFillers || startsLikeDoc || hasTypicalPattern || line.length >= 28;
  });
  
  console.log('MRZ candidates:', mrzCandidates);
  
  return mrzCandidates;
}

/**
 * Parse date from MRZ format (YYMMDD)
 */
function parseMRZDate(dateStr, isExpiry = false) {
  if (!dateStr || dateStr.length < 6) return null;
  
  // Extract just digits
  const digits = dateStr.replace(/[^0-9]/g, '').substring(0, 6);
  if (digits.length < 6) return null;
  
  const year = parseInt(digits.substring(0, 2), 10);
  const month = parseInt(digits.substring(2, 4), 10);
  const day = parseInt(digits.substring(4, 6), 10);
  
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  
  // Determine century
  let fullYear;
  if (isExpiry) {
    fullYear = year + 2000; // Expiry dates are usually future
  } else {
    const currentYearShort = new Date().getFullYear() % 100;
    fullYear = year > currentYearShort ? 1900 + year : 2000 + year;
  }
  
  return `${fullYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Parse name from MRZ format (SURNAME<<FIRSTNAME or similar)
 */
function parseMRZName(nameField) {
  if (!nameField) return { cognome: '', nome: '' };
  
  // Clean the field
  let cleaned = nameField.replace(/[^A-Z<]/g, '');
  
  // Names are separated by << between surname and given names
  const parts = cleaned.split(/<<+/).filter(p => p && p !== '<');
  
  if (parts.length === 0) return { cognome: '', nome: '' };
  
  const cognome = parts[0].replace(/<+/g, ' ').trim();
  const nome = parts.length > 1 ? parts.slice(1).join(' ').replace(/<+/g, ' ').trim() : '';
  
  // Capitalize properly
  const capitalize = (str) => str.split(' ')
    .filter(w => w)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
  
  return {
    cognome: capitalize(cognome),
    nome: capitalize(nome)
  };
}

/**
 * Try to parse TD1 format (Italian CIE - 3 lines of ~30 chars)
 * Line 1: I<ITA + Document number + optional data
 * Line 2: DOB (6) + Check (1) + Sex (1) + Expiry (6) + Check (1) + Nationality (3) + optional
 * Line 3: Name (SURNAME<<FIRSTNAME)
 */
function tryParseTD1(lines) {
  if (lines.length < 2) return null;
  
  let result = {
    format: 'TD1',
    tipo_documento: 'carta_identita',
    numero_documento: '',
    cognome: '',
    nome: '',
    data_nascita: null,
    scadenza_documento: null,
    nazionalita: 'Italiana',
    sesso: '',
    confidence: 0
  };
  
  // Look for Italian ID pattern in first lines
  for (let i = 0; i < Math.min(lines.length, 4); i++) {
    const line = lines[i];
    
    // Line with I<ITA or IDITA or similar - document type line
    if (/^I[<D]?ITA/.test(line) || /^I[<D][A-Z]{3}/.test(line)) {
      // Extract document number (usually after country code)
      const docMatch = line.match(/ITA([A-Z0-9]{7,12})/);
      if (docMatch) {
        result.numero_documento = docMatch[1].replace(/<+$/, '');
        result.confidence += 20;
      }
    }
    
    // Line with date of birth pattern: YYMMDD + check + M/F + YYMMDD
    const datePattern = line.match(/([0-9]{6})[0-9]?([MF<])([0-9]{6})/);
    if (datePattern) {
      result.data_nascita = parseMRZDate(datePattern[1], false);
      result.sesso = datePattern[2] === 'M' ? 'M' : (datePattern[2] === 'F' ? 'F' : '');
      result.scadenza_documento = parseMRZDate(datePattern[3], true);
      result.confidence += 30;
      
      // Look for nationality after expiry
      const natMatch = line.match(/[0-9]{6}[0-9]?([A-Z]{3})/);
      if (natMatch && COUNTRY_TO_NATIONALITY[natMatch[1]]) {
        result.nazionalita = COUNTRY_TO_NATIONALITY[natMatch[1]];
      }
    }
    
    // Line with name (contains << separator)
    if (line.includes('<<') || (line.match(/<</g) || []).length >= 1) {
      const { cognome, nome } = parseMRZName(line);
      if (cognome) {
        result.cognome = cognome;
        result.nome = nome;
        result.confidence += 25;
      }
    }
  }
  
  // Alternative: look for name pattern with < separators anywhere
  if (!result.cognome) {
    for (const line of lines) {
      if (line.length >= 20 && /[A-Z]+<+[A-Z]+/.test(line)) {
        const { cognome, nome } = parseMRZName(line);
        if (cognome && cognome.length > 1) {
          result.cognome = cognome;
          result.nome = nome;
          result.confidence += 15;
          break;
        }
      }
    }
  }
  
  return result.confidence >= 25 ? result : null;
}

/**
 * Try to parse TD3 format (Passport - 2 lines of 44 chars)
 * Line 1: P<COUNTRY + SURNAME<<FIRSTNAME
 * Line 2: Doc number + Check + Nationality + DOB + Check + Sex + Expiry + Check + Personal + Check
 */
function tryParseTD3(lines) {
  if (lines.length < 2) return null;
  
  let result = {
    format: 'TD3',
    tipo_documento: 'passaporto',
    numero_documento: '',
    cognome: '',
    nome: '',
    data_nascita: null,
    scadenza_documento: null,
    nazionalita: '',
    sesso: '',
    confidence: 0
  };
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Passport type line (P<COUNTRY + Name)
    if (/^P[<A-Z]/.test(line) && line.length >= 30) {
      // Extract country code
      const countryMatch = line.match(/^P[<]?([A-Z]{3})/);
      if (countryMatch) {
        result.nazionalita = COUNTRY_TO_NATIONALITY[countryMatch[1]] || countryMatch[1];
        result.confidence += 15;
      }
      
      // Extract name (everything after country)
      const nameStart = line.indexOf(countryMatch ? countryMatch[0] : 'P<') + (countryMatch ? countryMatch[0].length : 2);
      const namePart = line.substring(nameStart);
      const { cognome, nome } = parseMRZName(namePart);
      if (cognome) {
        result.cognome = cognome;
        result.nome = nome;
        result.confidence += 25;
      }
    }
    
    // Data line (contains document number, dates, etc.)
    // Pattern: 9 char doc + 1 check + 3 nationality + 6 DOB + 1 check + 1 sex + 6 expiry
    const dataMatch = line.match(/([A-Z0-9]{9})[0-9]([A-Z]{3})([0-9]{6})[0-9]([MF])([0-9]{6})/);
    if (dataMatch) {
      result.numero_documento = dataMatch[1].replace(/<+$/, '');
      if (COUNTRY_TO_NATIONALITY[dataMatch[2]]) {
        result.nazionalita = COUNTRY_TO_NATIONALITY[dataMatch[2]];
      }
      result.data_nascita = parseMRZDate(dataMatch[3], false);
      result.sesso = dataMatch[4];
      result.scadenza_documento = parseMRZDate(dataMatch[5], true);
      result.confidence += 35;
    }
  }
  
  return result.confidence >= 30 ? result : null;
}

/**
 * Try to extract data using more aggressive pattern matching
 * Works even when OCR quality is poor
 */
function tryAggressiveParse(text) {
  console.log('Trying aggressive parse on:', text);
  
  const result = {
    format: 'AGGRESSIVE',
    tipo_documento: 'carta_identita',
    numero_documento: '',
    cognome: '',
    nome: '',
    data_nascita: null,
    scadenza_documento: null,
    nazionalita: 'Italiana',
    sesso: '',
    confidence: 0
  };
  
  const cleanText = text.toUpperCase();
  
  // Try to find Italian document number pattern (2 letters + 7 alphanumeric)
  // CIE format: CA00000AA or similar
  const docNumPatterns = [
    /\b([A-Z]{2}[0-9]{5}[A-Z]{2})\b/,  // CIE format: CA12345AB
    /\b([A-Z]{2}[0-9]{7})\b/,           // Alternative: CA1234567
    /\b([A-Z][0-9]{8})\b/,              // Old format: A12345678
    /ITA([A-Z0-9]{7,10})/                // From MRZ: ITA followed by doc num
  ];
  
  for (const pattern of docNumPatterns) {
    const match = cleanText.match(pattern);
    if (match) {
      result.numero_documento = match[1];
      result.confidence += 20;
      break;
    }
  }
  
  // Try to find dates in various formats
  // YYMMDD or DDMMYY
  const datePatterns = [
    /([0-9]{2})(0[1-9]|1[0-2])([0-2][0-9]|3[01])/g,  // YYMMDD
    /([0-2][0-9]|3[01])(0[1-9]|1[0-2])([0-9]{2})/g   // DDMMYY
  ];
  
  const dates = [];
  for (const pattern of datePatterns) {
    let match;
    while ((match = pattern.exec(cleanText)) !== null) {
      const dateStr = match[0];
      // Try both interpretations
      const asYYMMDD = parseMRZDate(dateStr, false);
      if (asYYMMDD) {
        dates.push({ date: asYYMMDD, raw: dateStr });
      }
    }
  }
  
  console.log('Found dates:', dates);
  
  // First valid-looking date is probably DOB, second is expiry
  if (dates.length >= 1) {
    result.data_nascita = dates[0].date;
    result.confidence += 15;
  }
  if (dates.length >= 2) {
    result.scadenza_documento = dates[1].date;
    result.confidence += 10;
  }
  
  // Try to find name pattern - look for << separator or COGNOME/NOME keywords
  const namePatterns = [
    /([A-Z]+)<<([A-Z]+)/,           // MRZ style
    /([A-Z]{2,})<([A-Z]{2,})/,      // Simpler version
    /COGNOME[:\s]*([A-Z]+)/i,       // Labeled
    /NOME[:\s]*([A-Z]+)/i           // Labeled
  ];
  
  for (const pattern of namePatterns) {
    const match = cleanText.match(pattern);
    if (match) {
      if (pattern.source.includes('<<') || pattern.source.includes('<')) {
        result.cognome = match[1].replace(/<+/g, ' ').trim();
        result.nome = match[2] ? match[2].replace(/<+/g, ' ').trim() : '';
      } else if (pattern.source.includes('COGNOME')) {
        result.cognome = match[1];
      } else if (pattern.source.includes('NOME')) {
        result.nome = match[1];
      }
      result.confidence += 15;
    }
  }
  
  // Capitalize names
  const capitalize = (str) => str.split(' ')
    .filter(w => w)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
  
  if (result.cognome) result.cognome = capitalize(result.cognome);
  if (result.nome) result.nome = capitalize(result.nome);
  
  // Look for sex indicator
  if (/\b[MF]\b/.test(cleanText) || /\bMASCHIO\b/i.test(cleanText) || /\bFEMMINA\b/i.test(cleanText)) {
    result.sesso = /M|MASCHIO/i.test(cleanText) ? 'M' : 'F';
    result.confidence += 5;
  }
  
  // Look for nationality
  for (const [code, nat] of Object.entries(COUNTRY_TO_NATIONALITY)) {
    if (cleanText.includes(code)) {
      result.nazionalita = nat;
      result.confidence += 5;
      break;
    }
  }
  
  console.log('Aggressive parse result:', result);
  
  return result.confidence >= 20 ? result : null;
}

/**
 * Main function: Parse MRZ from OCR text
 * @param {string} ocrText - Raw text from OCR
 * @returns {object|null} - Parsed data or null if parsing failed
 */
export function parseMRZ(ocrText) {
  if (!ocrText || typeof ocrText !== 'string') {
    console.log('Invalid OCR text input');
    return null;
  }
  
  console.log('=== MRZ PARSER START ===');
  console.log('Input length:', ocrText.length);
  
  // First, try to find MRZ-like lines
  const mrzLines = findMRZLines(ocrText);
  
  // Try TD3 (Passport) first - more structured
  let result = tryParseTD3(mrzLines);
  if (result && result.confidence >= 30) {
    console.log('Parsed as TD3 (Passport):', result);
    return result;
  }
  
  // Try TD1 (ID Card like CIE)
  result = tryParseTD1(mrzLines);
  if (result && result.confidence >= 25) {
    console.log('Parsed as TD1 (ID Card):', result);
    return result;
  }
  
  // Try aggressive parsing as fallback
  result = tryAggressiveParse(ocrText);
  if (result && result.confidence >= 20) {
    console.log('Parsed with aggressive method:', result);
    return result;
  }
  
  console.log('=== MRZ PARSER: NO VALID DATA FOUND ===');
  return null;
}

/**
 * Validate if text looks like it might contain MRZ data
 */
export function containsMRZ(text) {
  if (!text) return false;
  
  const upper = text.toUpperCase();
  
  // Check for typical MRZ indicators
  const hasFillers = upper.includes('<<') || upper.includes('<');
  const hasDocType = /^[IPC][<A-Z]/.test(upper);
  const hasCountryCode = /\b(ITA|DEU|FRA|GBR|ESP|USA)\b/.test(upper);
  const hasDatePattern = /[0-9]{6}/.test(upper);
  
  return hasFillers || hasDocType || hasCountryCode || hasDatePattern;
}

export default { parseMRZ, containsMRZ };
